import type { ReactElement } from 'react';
import type { ExportEintrag } from '@mcikm-vote/shared';

import { exportDateiPfad } from '../admin/exporteApi';
import { formatiereZeitpunkt } from '../formatierung';
import { Knopf } from './Knopf';

/**
 * Eine Zeile je Exportformat mit den vier Angaben aus §27.3: ob erzeugt, Erstellungszeit,
 * Dateigröße und ob die Datei noch vorhanden ist.
 *
 * Die Komponente wird sowohl auf der Seite „Ergebnisse und Exporte" als auch auf der Seite einer
 * einzelnen Abstimmung verwendet. Beide zeigen denselben Sachverhalt — läge er zweimal im Code,
 * könnten sie unterschiedliche Zustände behaupten.
 */

/** Dateigröße in einer für Menschen lesbaren Form. */
export function formatiereGroesse(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1).replace('.', ',')} kB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

export function ExportFormatZeile({
  beschriftung,
  eintrag,
  laeuft,
  onErzeugen,
  onLoeschen,
}: {
  beschriftung: string;
  /** `null`, solange in diesem Format nie exportiert wurde. */
  eintrag: ExportEintrag | null;
  laeuft: boolean;
  onErzeugen: () => void;
  onLoeschen: (eintrag: ExportEintrag) => void;
}): ReactElement {
  const vorhanden = eintrag !== null && eintrag.dateiVorhanden;

  return (
    // Eine benannte Gruppe je Format: Ein Bildschirmleser sagt damit an, worauf sich
    // „Erzeugen" oder „Löschen" bezieht — die Schaltflächen tragen sonst überall denselben Text.
    <div
      role="group"
      aria-label={`Export als ${beschriftung}`}
      className="border-rand flex flex-wrap items-center gap-3 border-t py-2 first:border-t-0"
    >
      <span className="w-16 font-medium">{beschriftung}</span>

      <span className="text-text-gedaempft grow text-sm">
        {eintrag === null ? (
          'noch nicht exportiert'
        ) : (
          <>
            erzeugt am {formatiereZeitpunkt(eintrag.erstelltAm)}
            {/* §27.4: Die frühere Erstellung bleibt nachvollziehbar, auch ohne Datei. */}
            {vorhanden
              ? eintrag.groesseBytes !== null && ` · ${formatiereGroesse(eintrag.groesseBytes)}`
              : ' · Datei nicht mehr vorhanden'}
          </>
        )}
      </span>

      <div className="flex flex-wrap gap-2">
        {vorhanden && (
          // Der Browser lädt die Datei selbst; die Kopfzeile `Content-Disposition` bestimmt den
          // Namen. Ein `fetch` müsste sie erst vollständig in den Arbeitsspeicher holen.
          <a
            href={exportDateiPfad(eintrag.id)}
            className="border-rand bg-flaeche-gedaempft hover:bg-rand min-h-touchflaeche inline-flex items-center rounded-md border px-4 font-medium transition"
          >
            Herunterladen
          </a>
        )}

        <Knopf art={vorhanden ? 'sekundaer' : 'primaer'} onClick={onErzeugen} disabled={laeuft}>
          {vorhanden ? 'Neu erzeugen' : 'Erzeugen'}
        </Knopf>

        {vorhanden && (
          <Knopf
            art="sekundaer"
            onClick={() => {
              onLoeschen(eintrag);
            }}
            disabled={laeuft}
          >
            Löschen
          </Knopf>
        )}
      </div>
    </div>
  );
}
